import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Typography,
  TextField,
  TablePagination,
  Snackbar,
  Alert,
  InputAdornment,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  useGetDevicesQuery,
  useGetBulkStatusQuery,
  useBulkRebootMutation,
  useGetOrganizationsQuery,
} from "../../redux/slices/djangoApi";
import useAuth from "../../hooks/useAuth";
import { startCycle } from "../../redux/slices/powerCycleSlice";
import SummaryCards from "./SummaryCards";
import DeviceMap from "./DeviceMap";
import DeviceCardGrid from "./DeviceCardGrid";
import DeviceListView from "./DeviceListView";
import DeviceCycleTracker from "./DeviceCycleTracker";
import BulkActionBar from "./BulkActionBar";

const DevicesDashboard = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status");
  const orgFilter = searchParams.get("organization") || "";
  const cyclingDevices = useSelector((state) =>
    Object.entries(state.powerCycle.devices)
      .filter(([, v]) => v.phase && v.phase !== "complete")
      .map(([sn]) => sn)
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [snackbar, setSnackbar] = useState(null);

  // Reset to first page when status filter changes
  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Organization list for filter dropdown (staff only)
  const { data: orgsData } = useGetOrganizationsQuery(undefined, {
    skip: !user?.is_staff,
  });
  const organizations = orgsData?.results || orgsData || [];

  // Fast initial load — lightweight list, no EMQX
  const { data: devicesData, isLoading: devicesLoading } = useGetDevicesQuery({
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    search: debouncedSearch,
    ...(orgFilter && { organization: orgFilter }),
    ...(statusFilter && { connection_status: statusFilter }),
  });

  // Live status — WebSocket pushes updates into this cache automatically
  const { data: bulkData, isLoading: statusLoading } = useGetBulkStatusQuery(
    orgFilter ? { organization: orgFilter } : undefined
  );

  const [bulkReboot, { isLoading: isRebooting }] = useBulkRebootMutation();

  const totalCount = devicesData?.count || 0;
  const summary = bulkData?.summary || {
    total: 0,
    online: 0,
    offline: 0,
  };

  // Merge device metadata with live status
  const enrichedDevices = useMemo(() => {
    const devices = devicesData?.results || [];
    const statuses = bulkData?.statuses || {};
    return devices.map((device) => ({
      ...device,
      ...(statuses[device.serial_number] || {}),
    }));
  }, [devicesData, bulkData]);

  const handleSelectDevice = (sn) => {
    setSelectedDevices((prev) =>
      prev.includes(sn) ? prev.filter((s) => s !== sn) : [...prev, sn]
    );
  };

  const handleBulkReboot = async () => {
    try {
      const result = await bulkReboot({
        serial_numbers: selectedDevices,
      }).unwrap();
      // Track each initiated device's power cycle in Redux
      (result.initiated || []).forEach((sn) => dispatch(startCycle(sn)));
      setSelectedDevices([]);
      setSnackbar({
        severity: "success",
        message: `Power cycle initiated for ${
          result.initiated?.length || 0
        } device(s).`,
      });
    } catch (err) {
      setSnackbar({
        severity: "error",
        message: "Failed to initiate bulk reboot.",
      });
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Typography variant="h4" fontWeight="bold">
          {statusFilter
            ? `${
                statusFilter.charAt(0) + statusFilter.slice(1).toLowerCase()
              } Devices`
            : "Device Dashboard"}
        </Typography>
        {statusFilter && (
          <Chip
            label={`Filtered: ${statusFilter}`}
            color={statusFilter === "ONLINE" ? "success" : "error"}
            onDelete={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("status");
              setSearchParams(next);
            }}
            size="small"
          />
        )}
        {orgFilter && (
          <Chip
            label={`Org: ${
              organizations.find((o) => String(o.id) === orgFilter)?.name ||
              orgFilter
            }`}
            color="primary"
            variant="outlined"
            onDelete={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("organization");
              setSearchParams(next);
            }}
            size="small"
          />
        )}
      </Box>

      <SummaryCards summary={summary} isLoading={statusLoading} />

      {!devicesLoading && enrichedDevices.length > 0 && (
        <DeviceMap devices={enrichedDevices} />
      )}

      <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
        <TextField
          placeholder="Search by name, location, or serial number..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="grid">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="list">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        {user?.is_staff && organizations.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={orgFilter}
              label="Organization"
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                if (e.target.value) {
                  next.set("organization", e.target.value);
                } else {
                  next.delete("organization");
                }
                setSearchParams(next);
                setPage(0);
              }}
            >
              <MenuItem value="">All Organizations</MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.id} value={String(org.id)}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {viewMode === "list" ? (
        <DeviceListView
          devices={enrichedDevices}
          selectedDevices={selectedDevices}
          onSelectDevice={handleSelectDevice}
          isLoading={devicesLoading}
        />
      ) : (
        <DeviceCardGrid
          devices={enrichedDevices}
          selectedDevices={selectedDevices}
          onSelectDevice={handleSelectDevice}
          isLoading={devicesLoading}
        />
      )}

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />

      <BulkActionBar
        selectedCount={selectedDevices.length}
        onReboot={handleBulkReboot}
        onClear={() => setSelectedDevices([])}
        isRebooting={isRebooting}
      />

      {/* Hidden trackers that drive phase transitions for cycling devices */}
      {cyclingDevices.map((sn) => {
        const device = enrichedDevices.find((d) => d.serial_number === sn);
        return device ? (
          <DeviceCycleTracker key={sn} serialNumber={sn} device={device} />
        ) : null;
      })}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar && (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Container>
  );
};

export default DevicesDashboard;
